import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/feature/Navbar';

const PLAN_INFO: Record<string, { label: string; accent: string; icon: string; features: string[] }> = {
  premium: {
    label: 'Premium',
    accent: '#00e87a',
    icon: 'ri-vip-crown-line',
    features: [
      'Estadísticas y tendencias avanzadas',
      'Alertas Dropping Odds (20/día)',
      'Gestor de bankroll personal',
      'Filtros cruzados sin límite',
      'Comparador completo (6 casas)',
      'Publicar picks en el foro',
    ],
  },
  elite: {
    label: 'Elite',
    accent: '#ffd700',
    icon: 'ri-vip-diamond-line',
    features: [
      'Todo lo de Premium',
      'Alertas SMS ilimitadas',
      'Ligas privadas ilimitadas',
      'Badge Elite verificado en perfil',
      'Datos históricos (5 años)',
      'Soporte prioritario 24/7',
    ],
  },
};

export default function PagoExitosoPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshProfile, isRegistered, loading } = useAuth();
  const [refreshed, setRefreshed] = useState(false);
  const [countdown, setCountdown] = useState(10);

  const planKey = searchParams.get('plan') ?? 'premium';
  const planInfo = PLAN_INFO[planKey] ?? PLAN_INFO.premium;

  useEffect(() => {
    if (!refreshed) {
      const refresh = async () => {
        // Small delay to let Stripe webhook process
        await new Promise((r) => setTimeout(r, 2000));
        await refreshProfile();
        setRefreshed(true);
      };
      refresh();
    }
  }, []);

  // Auto-redirect countdown
  useEffect(() => {
    if (!refreshed) return;
    if (countdown <= 0) {
      navigate('/perfil', { replace: true });
      return;
    }
    const t = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(t);
  }, [refreshed, countdown, navigate]);

  // If user somehow lands here without auth, redirect home
  useEffect(() => {
    if (!loading && !isRegistered) {
      navigate('/', { replace: true });
    }
  }, [loading, isRegistered, navigate]);

  return (
    <div className="bg-[#0d0f14] min-h-screen">
      <Navbar />
      <div className="pt-16 min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-lg">
          {/* Success card */}
          <div
            className="bg-[#151719] border rounded-2xl overflow-hidden"
            style={{ borderColor: `${planInfo.accent}40` }}
          >
            {/* Top glow bar */}
            <div
              className="h-1 w-full"
              style={{ background: `linear-gradient(90deg, transparent, ${planInfo.accent}, transparent)` }}
            />

            <div className="p-8 text-center">
              {/* Animated checkmark */}
              <div
                className="w-20 h-20 flex items-center justify-center rounded-full mx-auto mb-5"
                style={{ background: `${planInfo.accent}15`, border: `2px solid ${planInfo.accent}40` }}
              >
                {!refreshed ? (
                  <i className="ri-loader-4-line text-3xl animate-spin" style={{ color: planInfo.accent }}></i>
                ) : (
                  <i className="ri-checkbox-circle-fill text-4xl" style={{ color: planInfo.accent }}></i>
                )}
              </div>

              <span
                className="text-xs font-bold uppercase tracking-widest"
                style={{ color: planInfo.accent }}
              >
                Pago completado
              </span>
              <h1 className="text-white text-3xl font-black mt-2 mb-2">
                ¡Bienvenido al plan{' '}
                <span style={{ color: planInfo.accent }}>{planInfo.label}</span>!
              </h1>
              <p className="text-[#6b7280] text-sm mb-6">
                {refreshed
                  ? 'Tu suscripción ya está activa. Empieza a disfrutar de todas las funciones.'
                  : 'Activando tu plan... Un momento por favor.'}
              </p>

              {/* Plan features */}
              <div className="bg-[#0d0f14] rounded-xl p-5 mb-6 text-left">
                <p className="text-[#6b7280] text-xs uppercase tracking-wider font-bold mb-3">
                  Lo que ya tienes disponible
                </p>
                <ul className="space-y-2.5">
                  {planInfo.features.map((f) => (
                    <li key={f} className="flex items-center gap-3 text-sm text-[#a8b2c1]">
                      <div
                        className="w-4 h-4 flex items-center justify-center flex-shrink-0"
                        style={{ color: planInfo.accent }}
                      >
                        <i className="ri-check-line text-sm"></i>
                      </div>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/perfil"
                  className="flex-1 py-3 rounded-xl text-sm font-bold text-center cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 transition-all"
                  style={{ background: planInfo.accent, color: '#000' }}
                >
                  <i className="ri-user-line text-sm"></i>
                  Ir a mi perfil
                </Link>
                <Link
                  to="/estadisticas"
                  className="flex-1 py-3 bg-[#1e2028] border border-[#2a2d3a] text-white rounded-xl text-sm font-bold text-center cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 hover:bg-white/5 transition-all"
                >
                  <i className="ri-bar-chart-2-line text-sm"></i>
                  Ver estadísticas
                </Link>
              </div>

              {/* Countdown */}
              {refreshed && (
                <p className="text-[#4a5568] text-xs mt-4">
                  Redirigiendo al perfil en{' '}
                  <span className="text-[#a8b2c1] font-bold">{countdown}s</span>...
                </p>
              )}
            </div>
          </div>

          {/* Security badge */}
          <div className="flex items-center justify-center gap-2 mt-5 text-[#4a5568] text-xs">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-shield-check-line text-sm"></i>
            </div>
            Pago procesado de forma segura por Stripe — sin guardar datos de tarjeta
          </div>
        </div>
      </div>
    </div>
  );
}
